//src/hooks/useCancelSubscription.js


import { useState, useCallback } from "react";
import { apiUrl } from "../config/apiConfig";
import { cancelReasons } from "../data/services";
import {
  unsubscribePromotionSubscription,
  unsubscribeUfoneSubscription,
  unsubscribeJazzSubscription,
  unsubscribeWHSSubscription,
} from "../services/unsubscriptionApi";

export const getReasonLabel = (reasonId, isEn = true) => {
  const selected = cancelReasons.find((r) => r.id === reasonId);
  if (!selected) return reasonId || "No Longer Needed";
  return isEn ? selected.labelEn : (selected.labelRu || selected.labelEn);
};

/**
 * useCancelSubscription()
 * Centralised cancel/unsubscribe + refund logging logic.
 * Reused by CancelRefund.js (full page) and ZaraChatWidget (in-chat flow).
 */
function useCancelSubscription() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submitCancel = useCallback(async ({ svc, phone, network, reason, language }) => {
    setError("");

    if (!phone || !network) {
      const err = new Error("Unable to submit cancellation. Missing phone or network.");
      setError(err.message);
      throw err;
    }
    if (!reason) {
      const err = new Error("Please choose a reason for cancellation.");
      setError(err.message);
      throw err;
    }

    setSubmitting(true);
    try {
      const netLower = String(network || "").toLowerCase().trim();
      const isPromotionSubscription = Boolean(svc?.apiService) && netLower === "zong";
      const isUfoneSubscription = netLower === "ufone";
      const isJazzSubscription = netLower === "jazz";
      const isWHSSubscription = netLower === "easypaisa";

      let operatorMessage = null;

      if (isPromotionSubscription) {
        await unsubscribePromotionSubscription(phone, svc.apiService);
      } else if (isUfoneSubscription) {
        const ufoneResponse = await unsubscribeUfoneSubscription(phone);
        const responseCode = String(ufoneResponse?.data?.responseCode || "");
        const isUnsubscribed =
          String(ufoneResponse?.data?.IS_UN_SUB || "").trim() === "Y" || responseCode === "100";
        operatorMessage = isUnsubscribed
          ? "Your subscription has been cancelled successfully."
          : (ufoneResponse?.data?.message || "You are not currently subscribed to this service.");
      } else if (isJazzSubscription) {
        const jazzResponse = await unsubscribeJazzSubscription(phone, svc.productCode, svc.name, "Digital");
        const rawResult = String(jazzResponse?.data?.raw || "").toUpperCase();
        operatorMessage =
          rawResult === "SUCCESS"
            ? "Your subscription has been cancelled successfully."
            : (jazzResponse?.message || "Unable to confirm cancellation status.");
      } else if (isWHSSubscription) {
        const whsResponse = await unsubscribeWHSSubscription(svc.id);
        operatorMessage = "Your subscription has been cancelled successfully.";
      }

      const body = {
        phone,
        network,
        service_name: svc.name,
        service_type: "subscription",
        price_per_month: svc.pricePerMonth,
        reason: getReasonLabel(reason, language === "English"),
        deducted_amount: svc.totalDeducted || 0,
        complimentary_amount: 0,
        total_refund: svc.totalDeducted || 0,
        language,
        api_service: svc.apiService || null,
        subscription_source: svc.apiServiceLabel || null,
      };

      const res = await fetch(apiUrl("/cancellations"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Unable to submit cancellation. Please try again.");
      }

      return { ...json, operatorMessage, serviceStatus: operatorMessage ? "handled" : "backend" };
    } catch (err) {
      setError(err.message || "Unable to submit cancellation. Please try again.");
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setSubmitting(false);
    setError("");
  }, []);

  return { submitCancel, submitting, error, reset };
}

export default useCancelSubscription;